import GoogleMapReact from "google-map-react";
import { isEmpty, mapValues } from "lodash";

import { isDefined } from "@clipboard-health/util-ts";
import { Address } from "src/interface";
import { environmentConfig } from "@src/appV2/environment";

export const LA_CENTER = { lat: 34.0522, lng: -118.2437 };

interface AddressIdAndValues {
  id: string;
  values: string[];
}

const ADDRESS_ID_AND_VALUES: AddressIdAndValues[] = [
  { id: "streetNumber", values: ["street_number"] },
  { id: "streetName", values: ["street_address", "route"] },
  { id: "postalCode", values: ["postal_code"] },
  { id: "subpremise", values: ["subpremise"] },

  {
    id: "region",
    values: [
      "administrative_area_level_2",
      "administrative_area_level_4",
      "administrative_area_level_5",
      "sublocality_level_1",
      "sublocality_level_2",
      "sublocality_level_3",
    ],
  },
  {
    id: "city",
    values: ["locality", "neighborhood", "sublocality", "administrative_area_level_3"],
  },
  { id: "state", values: ["administrative_area_level_1"] },
  { id: "country", values: ["country"] },
];

export function getMapApi() {
  /**
   * FIXME: Find a type for this code.
   @ts-expect-error GoogleMapReact is not typed */
  return GoogleMapReact.googleMapLoader({
    key: environmentConfig.REACT_APP_GOOGLE_MAP_API,
    libraries: "places",
  });
}

export function getLocationFromPlace(place: google.maps.places.PlaceResult) {
  if (!isDefined(place.geometry)) {
    throw new Error("Must pass in defined `place`");
  }
  const { location } = place.geometry;
  if (!isDefined(location)) {
    return LA_CENTER;
  }
  return {
    lat: location.lat(),
    lng: location.lng(),
  };
}

function getAddressType(value: string): AddressIdAndValues | undefined {
  return ADDRESS_ID_AND_VALUES.find(({ values }) => values.includes(value));
}

type InternalParsedAddress = Record<
  string,
  {
    type?: string;
    value: string;
  }
>;
/**
 * Find an index of a subset of ADDRESS_ID_AND_VALUES.
 * Specifically, `region` can have multiple `values`.
 * This identifies the last `region` from the parsedAddress.
 */
const isLowLevelType = (
  parsedAddress: InternalParsedAddress,
  values: AddressIdAndValues["values"],
  type: string
) => {
  const address = parsedAddress[type];
  if (!address || !isDefined(address.type)) {
    return false;
  }

  /**
   *  * Note, this is really confusing, and probably not even right.
   * The original code was broken.
   * It used to reference `values.index(type)` which is invalid.
   * See: https://github.com/ClipboardHealth/cbh-admin-frontend/blob/7a5e59caf22114a2cdbeefe74a7c5026319754e7/src/utils/googleMap.ts#L68
   */
  return values.indexOf(type) > values.indexOf(address.type);
};

export type GeoAddress = Partial<Address>;
export function parseAddressComponents(
  addressComponents: google.maps.GeocoderAddressComponent[]
): GeoAddress {
  const address: InternalParsedAddress = {};
  addressComponents.forEach((component) => {
    // Find the first type that matches our address mappings
    const type = component.types.find((t) => getAddressType(t));
    if (!type) {
      return;
    }

    const addressType = getAddressType(type);

    if (isEmpty(addressType)) {
      return;
    }

    const isLowLevel = isLowLevelType(address, addressType.values, type);

    if (!isLowLevel) {
      address[addressType.id] = { type, value: component.long_name };
    }

    if (addressType.id === "state") {
      address.stateCode = { value: component.short_name };
    }
    if (addressType.id === "country") {
      address.countryCode = { value: component.short_name };
    }
  });

  const neighborhoodComponent = addressComponents.find((component) =>
    component.types.includes("neighborhood")
  );

  if (neighborhoodComponent) {
    address.neighborhood = { value: neighborhoodComponent.long_name };
  }

  return mapValues(address, ({ value }) => value);
}
